// Shared CSV/JSON parsing utilities for cap-table imports. Integration layer.

import type { ShareInstrument } from '@backsolve/shared-types';
import { ulid } from '@/id';

export type CsvRow = Record<string, string>;

export function parseCsv(text: string): CsvRow[] {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0]!.split(',').map((h) => h.trim().toLowerCase());
  const rows: CsvRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i]!.split(',');
    const row: CsvRow = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]!] = (cells[j] ?? '').trim();
    }
    rows.push(row);
  }
  return rows;
}

export function parseJsonOrCsv(file: string): CsvRow[] {
  try {
    const parsed = JSON.parse(file);
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    return arr.map((r: Record<string, unknown>) => {
      const obj: CsvRow = {};
      for (const [k, v] of Object.entries(r)) obj[k.toLowerCase()] = String(v ?? '');
      return obj;
    });
  } catch {
    return parseCsv(file);
  }
}

export function parseNumber(v: string | undefined): number {
  if (!v) return 0;
  const n = parseFloat(v.replace(/[$,]/g, ''));
  return isNaN(n) ? 0 : n;
}

export function mapKind(raw: string): ShareInstrument['kind'] {
  const k = raw.toLowerCase();
  if (k.includes('pref')) return 'preferred';
  if (k.includes('option')) return 'option';
  if (k.includes('warrant')) return 'warrant';
  if (k.includes('rsu') || k.includes('restricted')) return 'rsu';
  if (k.includes('pool') || k.includes('reserved')) return 'pool';
  return 'common';
}

export function buildInstrument(
  row: CsvRow,
  fieldMap: {
    name: string[];
    kind: string[];
    shares: string[];
    authorized: string[];
    issuePrice: string[];
    issueDate: string[];
    liquidationMultiple?: string[];
    strikePrice?: string[];
    exerciseDate?: string[];
  },
): ShareInstrument {
  const pick = (keys: string[]): string => {
    for (const k of keys) {
      const v = row[k];
      if (v !== undefined && v !== '') return v;
    }
    return '';
  };

  const kind = mapKind(pick(fieldMap.kind) || 'common');
  return {
    id: `inst_${ulid()}`,
    name: pick(fieldMap.name) || 'Unknown',
    kind,
    sharesOutstanding: parseNumber(pick(fieldMap.shares)),
    sharesAuthorized: parseNumber(pick(fieldMap.authorized)),
    issuePrice: parseNumber(pick(fieldMap.issuePrice)),
    issueDate: pick(fieldMap.issueDate) || null,
    liquidationMultiple: kind === 'preferred' && fieldMap.liquidationMultiple ? parseNumber(pick(fieldMap.liquidationMultiple) || '1') : null,
    participation: kind === 'preferred' ? 'nonParticipating' : null,
    participationCap: null,
    seniority: kind === 'preferred' ? 1 : null,
    conversionRatio: kind === 'preferred' ? 1 : null,
    strikePrice: (kind === 'option' || kind === 'warrant') && fieldMap.strikePrice ? parseNumber(pick(fieldMap.strikePrice)) : null,
    vestingMonths: kind === 'option' ? 48 : null,
    expirationDate: null,
    antiDilution: null,
    notes: '',
  };
}
